import { BuyerLink } from '@choco-admin/types';
import {
  CountryCode,
  getCountryCodeFromPhoneNumber,
  DEFAULT_LOCALE,
} from '@choco-admin/utils';
import { Divider } from '@heroui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { ProfilePicture } from '../../../../components/FormComponents';
import { FormFooter } from '../../../../components/FormFooter';
import { IsPuppyIndicator } from '../../../../components/IsPuppyIndicator';
import { BuyerSelectOption } from '../BuyerLinking/types';
import { OrderPreferencesField } from '../OrderPreferences';
import { SupplierSelectOption } from '../SupplierLinking/utils';

import {
  AdminActionsSection,
  BasicInformationSection,
  CognitoStateSection,
  CreateUserLinkingSection,
  LoginMethodSection,
  TwilioStateSection,
  UserLinkingSection,
  UserTypeSection,
} from './components';
import {
  useUserFormValidation,
  useProfilePicture,
  type UserFormData,
  type UserFormUser,
} from './hooks';

interface UserFormProps {
  initialData?: Partial<UserFormData>;
  onSubmit: (data: UserFormData) => Promise<void>;
  isSubmitting?: boolean;
  submitLabel?: string;
  isEditMode?: boolean;
  user?: UserFormUser | null; // For ProfilePicture backend integration and supplier data
  userBuyers?: BuyerLink[];
  onCancel?: () => void;
  onSignInAsUser?: () => void;
  disabled?: boolean; // Make form read-only
}

export const UserForm: React.FC<UserFormProps> = ({
  initialData,
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Save User',
  isEditMode = false,
  user = null,
  userBuyers = [],
  onCancel,
  onSignInAsUser,
  disabled = false,
}) => {
  // Country code state management - extract from phone number if available
  const getInitialCountryCode = (): CountryCode => {
    if (initialData?.countryCode) {
      return initialData.countryCode as CountryCode;
    }
    if (initialData?.phone) {
      return getCountryCodeFromPhoneNumber(initialData.phone) || 'US';
    }
    return 'US';
  };

  const [countryCode, setCountryCode] = useState<CountryCode>(
    getInitialCountryCode()
  );

  // Use validation hook
  const {
    validationSchema,
    isLoginEmailMethodSelected,
    isLoginPhoneMethodSelected,
    getInputSubLabel,
  } = useUserFormValidation(initialData?.loginMethod || 'phone');

  // Use profile picture hook (image change temporarily disabled)
  const profilePictureHook = useProfilePicture({
    user,
    onImageChange: () => {
      // TODO: Re-implement with a safer type
    },
  });

  // Helper function to get initial country code from phone
  const getCountryCodeFromPhone = (phone?: string | null): CountryCode => {
    if (phone && phone.startsWith('+')) {
      const detected = getCountryCodeFromPhoneNumber(phone);
      return detected || 'US';
    }
    return 'US';
  };

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      name: initialData?.name || '',
      email: initialData?.email || '',
      phone: initialData?.phone || '',
      locale: initialData?.locale || DEFAULT_LOCALE,
      loginMethod: initialData?.loginMethod || 'phone',
      isTest: initialData?.isTest || false,
      supplier: initialData?.supplier || false,
      linkedBuyers: initialData?.linkedBuyers || [],
      supplierId: initialData?.supplierId || '',
      countryCode: (initialData?.countryCode || 'US') as CountryCode,
      supportLogin: initialData?.supportLogin || false,
      whatsAppCountryCode: getCountryCodeFromPhone(
        initialData?.orderPreferences?.whatsApp
      ),
      smsCountryCode: getCountryCodeFromPhone(
        initialData?.orderPreferences?.sms
      ),
      orderPreferences: {
        pdf: initialData?.orderPreferences?.pdf || false,
        xlsx: initialData?.orderPreferences?.xlsx || false,
        sms: initialData?.orderPreferences?.sms || null,
        whatsApp: initialData?.orderPreferences?.whatsApp || null,
      },
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    clearErrors,
  } = form;
  const watchSupplier = watch('supplier');

  const handleFormSubmit = async (data: UserFormData) => {
    await onSubmit(data);
  };

  const handleSave = () => {
    handleSubmit(handleFormSubmit)();
  };

  return (
    <>
      {user?.isPuppy && <IsPuppyIndicator />}
      <form className="space-y-6">
        <LoginMethodSection
          control={control}
          errors={errors}
          isEditMode={isEditMode}
          disabled={disabled}
        />

        <BasicInformationSection
          control={control}
          errors={errors}
          countryCode={countryCode}
          onChangeCountryCode={setCountryCode}
          setValue={setValue}
          isLoginEmailMethodSelected={isLoginEmailMethodSelected}
          isLoginPhoneMethodSelected={isLoginPhoneMethodSelected}
          getInputSubLabel={getInputSubLabel}
          disabled={disabled}
        />

        <Divider />

        {/* User Linking */}
        {isEditMode && user ? (
          <UserLinkingSection
            user={user}
            userBuyers={userBuyers}
            disabled={disabled}
          />
        ) : (
          <CreateUserLinkingSection
            control={control}
            watch={watch}
            onBuyerSelect={(buyers: BuyerSelectOption[]) => {
              setValue('linkedBuyers', buyers);
            }}
            onSupplierSelect={(supplier: SupplierSelectOption | null) => {
              setValue('supplier', !!supplier);
              setValue('supplierId', supplier?.value || '');
            }}
            selectedBuyers={watch('linkedBuyers') || []}
            selectedSupplier={
              watch('supplierId') && watch('supplierId') !== ''
                ? {
                    value: watch('supplierId') || '',
                    label: watch('supplierName') || 'Selected Supplier',
                  }
                : null
            }
            disabled={disabled}
          />
        )}

        <UserTypeSection
          control={control}
          errors={errors}
          watch={watch}
          isEditMode={isEditMode}
          disabled={disabled}
        />

        {/* Profile Picture */}
        <div className="space-y-4">
          <ProfilePicture
            onImageSave={profilePictureHook.handleImageSave}
            user={
              user
                ? {
                    id: user.id,
                    name: user.name || undefined,
                    profileImage: user.profileImage
                      ? {
                          url: user.profileImage.url || undefined,
                          mediumSquare:
                            user.profileImage.mediumSquare || undefined,
                        }
                      : undefined,
                  }
                : undefined
            }
            isDisabled={isSubmitting || profilePictureHook.isUploading}
            data-testid="profile-picture-field"
          />
        </div>

        {/* Order Preferences */}
        <OrderPreferencesField
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
          clearErrors={clearErrors}
          disabled={disabled}
        />

        {!watchSupplier && (
          <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
            <p className="text-sm text-warning-800">
              <strong>Note:</strong> Users must be either test users, suppliers,
              or linked to buyers to be valid.
            </p>
          </div>
        )}

        {/* State Components - only show in edit mode */}
        {isEditMode && user?.id && (
          <>
            <CognitoStateSection userId={user.id} />
            <TwilioStateSection userId={user.id} />
          </>
        )}

        {/* Admin Actions */}
        <AdminActionsSection
          user={user}
          isEditMode={isEditMode}
          onSignInAsUser={onSignInAsUser}
        />

        <div className="pt-4">
          <FormFooter
            saveText={submitLabel}
            cancelText="Cancel"
            onSave={handleSave}
            onCancel={onCancel || (() => window.history.back())}
            isSaving={isSubmitting}
            isSaveDisabled={!isDirty || disabled}
            isCancelDisabled={disabled}
          />
        </div>
      </form>
    </>
  );
};
